import { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import { financeItemParams } from "../../../ConfirmShow/actions/confirmShowActionCreators";

const TOAST_OPTS = {
  position: toast.POSITION.TOP_CENTER,
  draggable: false,
  closeOnClick: false,
  autoClose: 5000,
  hideProgressBar: true,
};

// Refresh a v2 offer's settlement total after an on-settlement finance item
// changes — mirrors the legacy fetchOfferTotalDue dispatch.
const fetchOfferTotalDue = (team, offer, offerChanged) =>
  axios.get(`/teams/${team.id}/offers/${offer.id}/settlement.json`).then(({ data }) => {
    offerChanged({ ...offer, total_due: data.total_due, tax_withholding: data.tax_withholding });
  });

// Local replacement for the Redux finance-item slice the legacy ConfirmShow page
// wires through mapDispatchToProps. Replicates confirmShowReducer's finance state
// transitions and the action-creator axios calls so the shared ManageFinanceItems
// renders identically on the standalone Event Page tab. offerChanged mutates the
// offers state owned by FinancesTab (used for deposits + on-settlement totals).
// The legacy mutations also dispatch fetchProfitAndLoss; omitted here on purpose
// since P&L is its own Event Page tab with its own fetch.
export default function useFinanceItems(initialData, offerChanged) {
  const [financeItems, setFinanceItems] = useState(initialData.financeItems || []);
  const [financeCategories, setFinanceCategories] = useState(initialData.financeCategories || []);
  const [financeItemToEdit, setFinanceItemToEdit] = useState({});
  const [financeItemForForm, setFinanceItemForForm] = useState({});

  const financeItemsChanged = (items) => setFinanceItems(items);
  const financeCategoriesChanged = (cats) => setFinanceCategories(cats);
  const financeItemForFormChanged = (form) => setFinanceItemForForm(form);

  const financeItemToEditChanged = (item) => {
    setFinanceItemToEdit(item);
    if (item.id) setFinanceItems((prev) => prev.filter((fi) => fi.id));
  };

  const financeItemToEditCanceled = () => {
    setFinanceItemToEdit({});
    setFinanceItems((prev) => prev.filter((fi) => fi.id));
  };

  const fetchNewFinanceItem = (team, is_income) =>
    axios.get(`/teams/${team.id}/finance_items/new`).then(({ data }) => {
      data.payment_method = null;
      data.is_income = is_income;
      data.finance_line_items.forEach((lineItem) => { lineItem.category_id = ""; });

      setFinanceItems((prev) => [...prev, data]);
      setFinanceItemForForm(data);
      financeItemToEditChanged(data.finance_line_items[0]);
    });

  const refreshOffers = (team, offersToUpdate) =>
    (offersToUpdate || []).forEach((offer) => fetchOfferTotalDue(team, offer, offerChanged));

  const createFinanceItem = (csrfToken, team, confirm, financeItem, offersToUpdate) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    return axios
      .post(`/teams/${team.id}/finance_items`, financeItemParams(financeItem, confirm), {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(({ data }) => {
        setFinanceItems((prev) => prev.map((fi) => (fi.id ? fi : data)));
        financeItemToEditChanged({});
        refreshOffers(team, offersToUpdate);
      })
      .catch((error) => toast.error(error.response.data.join(", "), TOAST_OPTS));
  };

  const updateFinanceItem = (csrfToken, team, confirm, financeItem, offersToUpdate) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    return axios
      .patch(`/teams/${team.id}/finance_items/${financeItem.id}`, financeItemParams(financeItem), {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(({ data }) => {
        setFinanceItems((prev) => prev.map((fi) => (fi.id === data.id ? data : fi)));
        financeItemToEditChanged({});
        refreshOffers(team, offersToUpdate);
      })
      .catch((error) => toast.error(error.response.data.join(", "), TOAST_OPTS));
  };

  const deleteFinanceItem = (csrfToken, team, confirm, financeItem, offersToUpdate) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    return axios
      .delete(`/teams/${team.id}/finance_items/${financeItem.id}`)
      .then(({ data }) => {
        setFinanceItems((prev) => prev.filter((fi) => fi.id !== data.id));
        refreshOffers(team, offersToUpdate);
      });
  };

  const updateDeposit = (csrfToken, team, deposit, paid, successCallback) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    return axios
      .patch(`/teams/${team.id}/deposits/${deposit.id}`, { deposit: { paid } })
      .then(({ data }) => { if (successCallback) successCallback(data); })
      .catch((error) => toast.error(error.response?.data?.join?.(", ") || "Unable to update the deposit.", TOAST_OPTS));
  };

  const updateV3Deposit = (csrfToken, team, deposit, paid, successCallback) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    return axios
      .patch(`/teams/${team.id}/budget_deposits/v3/${deposit.id}`, { v3_budget_deposit: { paid } })
      .then(({ data }) => { if (successCallback) successCallback(data); })
      .catch((error) => toast.error(error.response?.data?.join?.(", ") || "Unable to update the deposit.", TOAST_OPTS));
  };

  return {
    financeItems,
    financeCategories,
    financeItemToEdit,
    financeItemForForm,
    financeItemsChanged,
    financeCategoriesChanged,
    financeItemForFormChanged,
    financeItemToEditChanged,
    financeItemToEditCanceled,
    fetchNewFinanceItem,
    createFinanceItem,
    updateFinanceItem,
    deleteFinanceItem,
    updateDeposit,
    updateV3Deposit,
  };
}
